import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Bell, BellOff, CheckCheck, LoaderCircle, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { translateUi } from "@i18n/config";

import { useTenant } from "@app/providers/tenant";
import {
  getAdminUnreadNotificationCount,
  listAdminNotifications,
  markAdminNotificationRead,
  markAllAdminNotificationsRead,
  type AdminNotification,
} from "../api/notifications";
import { useAdminPushNotifications } from "../hooks/useAdminPushNotifications";

const POLL_INTERVAL_MS = 60_000;

export function AdminNotificationBell() {
  const { t, i18n } = useTranslation();
  const { tenantId } = useTenant();
  const push = useAdminPushNotifications();
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState<AdminNotification[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const openRef = React.useRef(open);

  React.useEffect(() => {
    openRef.current = open;
  }, [open]);

  const refreshCount = React.useCallback(async () => {
    if (!tenantId) return;
    try {
      setUnreadCount(await getAdminUnreadNotificationCount());
    } catch {
      // Polling is best effort and must not disrupt admin navigation.
    }
  }, [tenantId]);

  const refreshItems = React.useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [result, count] = await Promise.all([
        listAdminNotifications(),
        getAdminUnreadNotificationCount(),
      ]);
      setItems(result.items);
      setUnreadCount(count);
    } catch {
      setLoadError(
        t("notificationsLoadError", "Unable to load notifications."),
      );
    } finally {
      setLoading(false);
    }
  }, [tenantId, t]);

  React.useEffect(() => {
    setItems([]);
    setUnreadCount(0);
    setOpen(false);
  }, [tenantId]);

  React.useEffect(() => {
    if (!tenantId) return;
    void refreshCount();
    const timer = window.setInterval(refreshCount, POLL_INTERVAL_MS);
    const handlePush = () => {
      void refreshCount();
      if (openRef.current) void refreshItems();
    };
    window.addEventListener("storizone:admin-push-received", handlePush);
    window.addEventListener(
      "storizone:admin-notifications-changed",
      handlePush,
    );
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("storizone:admin-push-received", handlePush);
      window.removeEventListener(
        "storizone:admin-notifications-changed",
        handlePush,
      );
    };
  }, [refreshCount, refreshItems, tenantId]);

  React.useEffect(() => {
    if (open) void refreshItems();
  }, [open, refreshItems]);

  React.useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node))
        setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  if (!tenantId) return null;

  const handleRead = async (item: AdminNotification) => {
    if (item.readAtUtc) return;
    setItems((current) =>
      current.map((candidate) =>
        candidate.id === item.id
          ? { ...candidate, readAtUtc: new Date().toISOString() }
          : candidate,
      ),
    );
    setUnreadCount((current) => Math.max(0, current - 1));
    try {
      await markAdminNotificationRead(item.id);
    } catch {
      void refreshItems();
    }
  };

  const handleReadAll = async () => {
    const now = new Date().toISOString();
    setItems((current) =>
      current.map((item) => ({ ...item, readAtUtc: item.readAtUtc || now })),
    );
    setUnreadCount(0);
    try {
      await markAllAdminNotificationsRead();
    } catch {
      void refreshItems();
    }
  };

  const isIosBrowser =
    typeof navigator !== "undefined" &&
    /iphone|ipad|ipod/i.test(navigator.userAgent) &&
    !window.matchMedia("(display-mode: standalone)").matches;
  const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className="relative rounded-md p-2 hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-primary/50"
        aria-label={t("notifications", "Notifications")}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((current) => !current)}
      >
        <Bell size={18} />
        {unreadCount > 0 ? (
          <span className="absolute -right-1 -top-1 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-background">
            {badgeLabel}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label={t("notifications", "Notifications")}
          className="fixed inset-x-3 top-16 z-50 overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-2xl sm:absolute sm:inset-auto sm:right-0 sm:top-11 sm:w-[24rem]"
        >
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div>
              <h2 className="font-semibold">
                {t("notifications", "Notifications")}
              </h2>
              <p className="text-xs text-muted-foreground">
                {unreadCount === 0
                  ? t("notificationsAllCaughtUp", "You are all caught up.")
                  : translateUi("countUnread", "{{count}} unread", {
                      count: unreadCount,
                    })}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 ? (
                <button
                  type="button"
                  onClick={() => void handleReadAll()}
                  className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={t("notificationsMarkAllRead", "Mark all as read")}
                >
                  <CheckCheck size={16} />
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label={t("close", "Close")}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="max-h-[24rem] overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-muted-foreground">
                <LoaderCircle className="animate-spin" size={16} />
                {t("loading", "Loading...")}
              </div>
            ) : loadError ? (
              <div className="px-4 py-8 text-center text-sm text-destructive">
                {loadError}
              </div>
            ) : items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                {t("notificationsEmpty", "No notifications yet.")}
              </div>
            ) : (
              items.map((item) => (
                <Link
                  key={item.id}
                  to={(item.actionUrl || "/notifications") as any}
                  onClick={() => {
                    void handleRead(item);
                    setOpen(false);
                  }}
                  className={`block border-b px-4 py-3 transition-colors last:border-b-0 hover:bg-muted/60 ${
                    item.readAtUtc
                      ? ""
                      : item.priority === "high"
                        ? "bg-red-500/5"
                        : "bg-primary/5"
                  }`}
                >
                  <div className="flex gap-3">
                    <span
                      className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                        item.readAtUtc
                          ? "bg-transparent"
                          : item.priority === "high"
                            ? "bg-red-500"
                            : "bg-primary"
                      }`}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">
                        {item.title}
                      </span>
                      <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
                        {item.body}
                      </span>
                      <span className="mt-1 block text-[11px] text-muted-foreground">
                        {formatTimestamp(item.createdAtUtc, i18n.language)}
                      </span>
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>

          <div className="border-t bg-muted/30 px-4 py-3">
            {push.isConfigured && push.isSupported ? (
              isIosBrowser ? (
                <p className="text-xs leading-5 text-muted-foreground">
                  {t(
                    "notificationsIosInstall",
                    "Add this admin app to your Home Screen, then open it there to enable push notifications.",
                  )}
                </p>
              ) : push.permission === "denied" ? (
                <p className="text-xs leading-5 text-muted-foreground">
                  {t(
                    "notificationsBlocked",
                    "Notifications are blocked in your browser settings.",
                  )}
                </p>
              ) : (
                <button
                  type="button"
                  disabled={push.isBusy}
                  onClick={() =>
                    void (push.subscription
                      ? push.unsubscribe()
                      : push.subscribe())
                  }
                  className="flex w-full items-center justify-center gap-2 rounded-md border bg-background px-3 py-2 text-xs font-semibold hover:bg-muted disabled:opacity-60"
                >
                  {push.isBusy ? (
                    <LoaderCircle className="animate-spin" size={16} />
                  ) : push.subscription ? (
                    <BellOff size={16} />
                  ) : (
                    <Bell size={16} />
                  )}
                  {push.subscription
                    ? t(
                        "notificationsDisablePush",
                        "Disable browser notifications",
                      )
                    : t(
                        "notificationsEnablePush",
                        "Enable browser notifications",
                      )}
                </button>
              )
            ) : (
              <p className="text-xs text-muted-foreground">
                {t(
                  "notificationsInAppOnly",
                  "In-app notifications are enabled.",
                )}
              </p>
            )}
            {push.error ? (
              <p className="mt-2 text-xs text-destructive">{push.error}</p>
            ) : null}
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="mt-2 block text-center text-xs font-medium text-primary hover:underline"
            >
              {t("notificationsViewAll", "View all notifications")}
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function formatTimestamp(value: string, locale: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale || "en", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
